import React from "react";
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AppBar,
  Toolbar,
  Link,
  Card,
  CardContent,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import {
  Shield,
  Grid as GridIcon,
  Clock,
  Lock,
  Heart,
  AlertTriangle,
} from "react-feather";
import theme from "./theme";
import ImageCarousel from "./components/ImageCarousel";
import "./App.css";

const CWS_URL = "https://chromewebstore.google.com/detail/shieldbug/aeiafdcfiklkbmdfcaobjapljoielcfn";

function App() {
  // Scrolls an element to just below the fixed AppBar. offsetTop is relative to
  // the nearest positioned ancestor, so it can't be used (or summed) to get a
  // document-relative position for nested sections.
  const scrollToId = (id) => {
    const element = document.getElementById(id);
    if (!element) return;
    const header = document.querySelector("header");
    const headerHeight = header ? header.offsetHeight : 64;
    const offsetPosition =
      element.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

    window.scrollTo({
      top: Math.max(offsetPosition, 0),
      behavior: "smooth",
    });
  };

  const scrollToContent = () => scrollToId("content");

  const scrollToSection = (sectionId) => scrollToId(sectionId);

  const carouselImages = [
    "/assets/Screen2.png",
    "/assets/Screen3.png",
    "/assets/Screen4.png",
    "/assets/Screen5.png",
  ];

  const carouselAltTexts = [
    "Set a schedule to stay focused",
    "Private and free",
    "Blocked. Stay on track.",
    "Take a deep breath.",
  ];

  const faqItems = [
    {
      question: "How do I install ShieldBee?",
      answer: <>Install it from the <Link href={CWS_URL} target="_blank" rel="noopener noreferrer" sx={{ color: "#ff9800" }}>Chrome Web Store</Link>. You need to be using Google Chrome.</>,
    },
    {
      question: "Is ShieldBee free?",
      answer:
        "Yes, ShieldBee is a free-to-use Chrome extension, and contains no advertising",
    },
    {
      question: "Is there a mobile app?",
      answer:
        "Right now there is no mobile app. It is at the top of the roadmap and coming soon.",
    },
    {
      question:
        'Why does ShieldBee need "Read and change your data on all websites" permissions?',
      answer:
        "Since ShieldBee needs to be able to block any website, it cannot restrict its access to a pre-determined list of websites. Other than blocking sites, ShieldBee does not read or manipulate your data in any way.",
    },
    {
      question: "Does ShieldBee use my data or track my activity?",
      answer:
        "ShieldBee does not monitor your data or track your browsing behavior in any way. Your distraction blocking settings are stored only on your own device and are not shared with the authors of this extension or any third parties.",
    },
    {
      question: "Who created ShieldBee?",
      answer:
        "ShieldBee is authored by Sam Ennis, a software engineer from Boston. It helps him nix compulsive news checking, doomscrolling, clickbait, and other distractions that leave your average internet user lost and demoralized. ShieldBee is a hobby project and will continue to receive updates over time. It is free, contains no advertising, and stores no data from you.",
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* Navigation Bar */}
      <AppBar position="fixed">
        <Toolbar>
          <Box
            component="img"
            src="/assets/Logo.svg"
            alt="ShieldBee Logo"
            sx={{
              width: 40,
              height: 40,
              marginRight: 1,
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              marginLeft: "10px",
              color: "#ff9800",
              fontWeight: 700,
              fontFamily:
                '"Quicksand", "Roboto", "Helvetica", "Arial", sans-serif',
            }}
          >
            ShieldBee
          </Typography>
          <Link
            component="button"
            variant="body1"
            onClick={() => scrollToSection("faq")}
            sx={{
              color: "rgba(255,255,255,0.7)",
              textDecoration: "none",
              marginRight: 3,
              cursor: "pointer",
              "&:hover": {
                color: "#ff9800",
              },
            }}
          >
            FAQ
          </Link>
          <Link
            component="button"
            variant="body1"
            onClick={() => scrollToSection("about")}
            sx={{
              color: "rgba(255,255,255,0.7)",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": {
                color: "#ff9800",
              },
            }}
          >
            About
          </Link>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #0f0a00 0%, #1e1500 100%)",
          paddingTop: "48px",
        }}
      >
        {/* Hero Section */}
        <Container
          maxWidth="md"
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            pt: 8,
            pb: 8,
          }}
        >
          {/* Large Logo */}
          <Box
            component="img"
            src="/assets/Logo.svg"
            alt="ShieldBee Logo"
            sx={{
              width: 140,
              height: 140,
              mb: 3,
              filter: "drop-shadow(0 8px 24px rgba(255, 152, 0, 0.5))",
            }}
          />

          <Typography variant="h1" component="h1" gutterBottom>
            Block online distractions, free
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.55)", fontSize: "1.2rem", mb: 4, maxWidth: 520 }}
          >
            Stop doomscrolling and compulsive news checking. ShieldBee blocks
            distracting sites so you can focus on what matters.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
            <Button
              variant="contained"
              size="large"
              href={CWS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Try ShieldBee — it's free
            </Button>
            <Button variant="outlined" size="large" onClick={scrollToContent}>
              Learn more
            </Button>
          </Box>
        </Container>

        {/* Content Section */}
        <Container
          maxWidth="lg"
          id="content"
          sx={{ pb: 3, minHeight: "100vh", pt: 8 }}
        >
          <Grid container spacing={4}>
            {/* Carousel Section */}
            <Grid item xs={12} md={12} id="about">
              <ImageCarousel
                images={carouselImages}
                altTexts={carouselAltTexts}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  boxShadow: "0 8px 32px rgba(10, 10, 10, 0.3)",
                  mb: 2,
                  "& .slick-slider": {
                    margin: "0",
                    padding: "0",
                  },
                  "& .slick-list": {
                    margin: "0",
                    padding: "0",
                  },
                  "& .slick-track": {
                    margin: "0",
                    padding: "0",
                  },
                }}
              />
            </Grid>

            {/* Features Section */}
            <Grid item xs={12} md={12} sx={{}}>
              <Typography variant="h2" gutterBottom sx={{ mt: 4, mb: 4 }}>
                What ShieldBee Does
              </Typography>
              <Grid container spacing={3}>
                {[
                  { icon: <Shield size={32} color="#ff9800" />, title: "Block Distracting Sites", body: "Block specific websites that leave you stressed or doomscrolling" },
                  { icon: <GridIcon size={32} color="#ff9800" />, title: "Block Categories", body: "Block distracting categories like gambling, news, or social media" },
                  { icon: <Clock size={32} color="#ff9800" />, title: "Set Schedules", body: "Set schedules for focus time to maintain productivity" },
                  { icon: <Lock size={32} color="#ff9800" />, title: "Password Protection", body: "Password-protect or time-lock settings to prevent workarounds" },
                  { icon: <AlertTriangle size={32} color="#ff9800" />, title: "Deterrent Mode", body: "Feeling bold? Enable giant spiders on your block page to deter yourself from visiting blocked sites" },
                  { icon: <Heart size={32} color="#ff9800" />, title: "Private & Free", body: "Your data is yours and is not collected or sold" },
                ].map((f, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Card sx={{ height: "100%", textAlign: "center", p: 2 }}>
                      <CardContent>
                        <Box sx={{
                          width: 68, height: 68, borderRadius: "50%",
                          background: "rgba(255, 152, 0, 0.12)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          mx: "auto", mb: 2,
                        }}>
                          {f.icon}
                        </Box>
                        <Typography variant="h6" gutterBottom>{f.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{f.body}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* FAQ Section */}
            <Grid item xs={12} md={12} id="faq">
              <Typography variant="h2" gutterBottom sx={{ mb: 4, mt: 4 }}>
                Frequently Asked
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {faqItems.map((item, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMore sx={{ color: "#ff9800" }} />}>
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "baseline" }}>
                        <Typography sx={{ color: "#ff9800", fontWeight: 800, fontSize: "0.8rem", letterSpacing: "0.08em", flexShrink: 0 }}>
                          Q
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {item.question}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "baseline" }}>
                        <Typography sx={{ color: "rgba(255,152,0,0.6)", fontWeight: 800, fontSize: "0.8rem", letterSpacing: "0.08em", flexShrink: 0 }}>
                          A
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {item.answer}
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Footer */}
        <Box sx={{ textAlign: "center", py: 4, color: "rgba(255,255,255,0.25)", fontSize: "0.85rem" }}>
          ShieldBee — made by Sam Ennis
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
